import { mdiChevronDown } from '@mdi/js';
import React, { useCallback } from 'react';
import { Spinner } from '../../base';
import { formatDate } from '../../../services/primitives/date-utils';
import * as S from './claim-timeline.styles';
import { IItemAction, IResourceTagProps, ITimelineEvent } from './claim-timeline.types';
import { TimelineEventChip } from './timeline-event-chip';
import { TimelineStatusTag } from './timeline-status-tag';
import {
    getTimelineEventLabel,
    getTimelineGridColumns,
    isTimelineEventExpandable,
} from './timeline-utils';
import { resolveTimelineExpandContent } from './timeline-event-detail';

export interface ITimelineRedesignListProps {
    items: ITimelineEvent[];
    appType: 'provider' | 'support';
    isLoading: boolean;
    expandedDetailIds: string[];
    onItemClick: (item: ITimelineEvent) => void;
    renderTags?: (tagProps: IResourceTagProps) => React.ReactNode;
    renderActions?: (actions: IItemAction[] | undefined, onChange: () => void) => React.ReactNode;
    onChange?: () => void;
    onExpandGrouped?: (item: ITimelineEvent) => void;
}

export const TimelineRedesignList = React.memo<ITimelineRedesignListProps>(
    ({ items, appType, isLoading, expandedDetailIds, onItemClick, renderTags, renderActions, onChange, onExpandGrouped }) => {
        const showTags = appType === 'support' && !!renderTags;
        const showActions = appType === 'support' && !!renderActions && !!onChange;
        const gridColumns = getTimelineGridColumns(appType, { showTags, showActions });

        const handleKeyDown = useCallback(
            (event: React.KeyboardEvent, item: ITimelineEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onItemClick(item);
                }
            },
            [onItemClick],
        );

        if (isLoading) {
            return (
                <S.RedesignLoading>
                    <Spinner />
                </S.RedesignLoading>
            );
        }

        return (
            <S.RedesignList>
                <S.RedesignHeader $columns={gridColumns}>
                    <span>Date Created</span>
                    <span>Event</span>
                    <span>Description</span>
                    {appType === 'provider' && <span>Type</span>}
                    {showTags && <span>Tags</span>}
                    {showActions && <span />}
                    <span />
                </S.RedesignHeader>

                <S.RedesignRows>
                    {items.map((item, index) => {
                        const expandable = isTimelineEventExpandable(item);
                        const isExpanded = !!item.id && expandedDetailIds.includes(item.id);
                        const isLast = index === items.length - 1;
                        const showExpandedBorder = !isLast || isExpanded;
                        const expandContent = isExpanded ? resolveTimelineExpandContent(item) : null;

                        return (
                            <S.RedesignRow key={item.id || `${item.created}-${index}`} $showBorder={showExpandedBorder}>
                                <S.RedesignRowHeader
                                    role={expandable ? 'button' : undefined}
                                    tabIndex={expandable ? 0 : undefined}
                                    $columns={gridColumns}
                                    $expanded={isExpanded}
                                    $expandable={expandable}
                                    onClick={() => onItemClick(item)}
                                    onKeyDown={event => handleKeyDown(event, item)}>
                                    <S.RedesignDot $isFirst={index === 0} />
                                    <S.RedesignDate>
                                        {item.created
                                            ? formatDate(item.created, 'MMM dd, yyyy hh:mm a')
                                            : ''}
                                    </S.RedesignDate>
                                    <TimelineEventChip
                                        icon={item.icon}
                                        label={getTimelineEventLabel(item)}
                                        badge={item.badge}
                                        secondaryType={item.secondaryType}
                                    />
                                    <S.RedesignDescription>
                                        {item.statusTag && (
                                            <TimelineStatusTag tone={item.statusTag.tone} label={item.statusTag.label} />
                                        )}
                                        {item.amount && <S.RedesignAmount>{item.amount}</S.RedesignAmount>}
                                        {item.description}
                                    </S.RedesignDescription>
                                    {appType === 'provider' && <S.RedesignType>{item.type}</S.RedesignType>}
                                    {showTags && (
                                        <S.RedesignTagsCell>
                                            {item.tag && renderTags ? renderTags(item.tag) : null}
                                        </S.RedesignTagsCell>
                                    )}
                                    {showActions && (
                                        <S.RedesignActionsCell>
                                            {renderActions && onChange
                                                ? renderActions(item.actions, onChange)
                                                : null}
                                        </S.RedesignActionsCell>
                                    )}
                                    <S.RedesignExpandIcon
                                        path={mdiChevronDown}
                                        size={0.75}
                                        $expanded={isExpanded}
                                        $visible={expandable}
                                    />
                                </S.RedesignRowHeader>

                                {expandContent && (
                                    <S.RedesignExpandPanel>
                                        {expandContent}
                                    </S.RedesignExpandPanel>
                                )}

                                {item.groupedItems && item.groupedItems.length > 0 && (
                                    <S.RowGroupWrapper
                                        onClick={() => onExpandGrouped?.(item)}>
                                        {(item.event || item.type) === 'Coding'
                                            ? `There are ${item.groupedItems.length} duplicate coding events. Click to show.`
                                            : `There are ${item.groupedItems.length} more similar events. Click to show.`}
                                    </S.RowGroupWrapper>
                                )}
                            </S.RedesignRow>
                        );
                    })}
                </S.RedesignRows>
            </S.RedesignList>
        );
    },
);
